/**
 * Vello icon vocabulary (S055; extraction contract §1/§3 `@vello/icons`, DD-03).
 *
 * The design source drew icons from Lucide **via a CDN** (design-debt DD-03: offline/licensing risk). This
 * package generalizes that to a **CDN-free, 1:1-swappable wrapper**: a curated, approved icon vocabulary
 * (typed names) that the host renders from a bundled/self-hosted icon set. `resolveIcon` returns a neutral
 * descriptor — the underlying set (`lucide` today) is a detail behind the `family` field, so swapping the
 * provider never changes a caller. Icons inherit the current text color (`currentColor`) — never a hardcoded
 * hex — and default to a token-derived size. An icon-only control still owns an accessible name from the
 * caller; the descriptor never invents copy.
 */
import { TAP_TARGET_MIN } from '@vello/tokens';
/** The approved icon set. Adding an icon is a deliberate vocabulary change, not an ad-hoc string. */
export const ICON_NAMES = [
    // navigation & search
    'search',
    'map-pin',
    'navigation',
    'route',
    'compass',
    'locate',
    // D736 retrofit: the "drag the map" hint of the pin picker (P28).
    'move',
    'menu',
    'chevron-left',
    'chevron-right',
    'chevron-up',
    'chevron-down',
    'arrow-left',
    'arrow-right',
    'x',
    'plus',
    'minus',
    // people & contact
    'user',
    'users',
    'phone',
    'message',
    'star',
    // mobility
    'car',
    'moto',
    'package',
    'clock',
    'flag',
    'home',
    'briefcase',
    'bookmark',
    // safety & status
    'shield',
    'alert-triangle',
    'check',
    'check-circle',
    'info',
    'ban',
    // money
    'wallet',
    'credit-card',
    'receipt',
    'gift',
    // misc
    'settings',
    'share',
    'eye',
    'eye-off',
    'refresh',
    'more-horizontal',
    // the native apps' design screens (D656: App Passageiro / App Motorista, Lucide names)
    'house',
    'list',
    'map',
    'bike',
    'utensils',
    'shopping-bag',
    'store',
    'truck',
    'hard-hat',
    'arrow-up-right',
    'corner-up-right',
    'move-up',
    'message-circle',
    'send',
    'share-2',
    'headphones',
    'siren',
    'circle-check',
    'circle-help',
    'circle-plus',
    'circle-x',
    'calendar',
    // D736 retrofit: the empty Reservas (P27).
    'calendar-clock',
    'timer',
    'camera',
    'download',
    'file-text',
    'banknote',
    'landmark',
    'ticket',
    'qr-code',
    'heart',
    'flame',
    'target',
    'zap',
    'award',
    'trending-up',
    'sliders-horizontal',
    'log-out',
    'moon',
    'sun',
    'bell',
    'lock',
    'pencil',
    'copy',
    'trash',
    // D776 retrofit (provider app): Verificações, the theme sheet.
    'shield-check',
    'smartphone',
    'sun-moon',
    // D826: voice messages in the ride chat (record, stop, play, pause).
    'mic',
    'square',
    'play',
    'pause',
];
/** Whether a string is an approved icon name. */
export function isIconName(value) {
    return ICON_NAMES.includes(value);
}
/** The default inline icon size (px), matching the 20px icons documented in the component contracts. */
export const DEFAULT_ICON_SIZE = 20;
export const DEFAULT_STROKE_WIDTH = 2;
/** Resolve an approved icon name to a render descriptor. Throws on an unknown name (fail closed). */
export function resolveIcon(name, opts = {}) {
    if (!isIconName(name))
        throw new Error(`unknown icon: ${String(name)}`);
    const sizePx = opts.sizePx ?? DEFAULT_ICON_SIZE;
    return {
        name,
        family: 'lucide',
        sizePx,
        strokeWidth: opts.strokeWidth ?? DEFAULT_STROKE_WIDTH,
        color: 'currentColor',
        minTargetPx: opts.interactive ? TAP_TARGET_MIN : sizePx,
    };
}
//# sourceMappingURL=icons.js.map